import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "step",
    "stepTitle",
    "stepCounter",
    "stepper",
    "stepperSegment",
    "stepperError",
    "prevButtonWrapper",
    "nextButtonWrapper",
    "saveButtonWrapper",
    "failScore",
    "conditionalField",
    "form",
    "header",
    "footer",
    "errorSummary",
    "scrollContainer",
  ]

  static values = {
    currentStep: { type: Number, default: 1 },
    totalSteps: { type: Number, default: 19 },
    stepTitles: Object,
  }

  connect() {
    this._onKeydown = this._onKeydown.bind(this)
    this._onPreviewViewChanged = this._onPreviewViewChanged.bind(this)
    document.addEventListener("keydown", this._onKeydown)
    document.addEventListener("qc-preview:view-changed", this._onPreviewViewChanged)
    this._reportDataOnLeft = this.currentStepValue > 1
    this._stepsWithErrors = this._snapshotErrorSteps()
    this._calculateFailScore()
    this._highlightNoAnswers()
    this._navigateToFirstErrorStep()
    this._updateUI()
    this._updateAllStepperSegments()
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
    document.removeEventListener("qc-preview:view-changed", this._onPreviewViewChanged)
  }

  nextStep() {
    if (this.currentStepValue < this.totalStepsValue) {
      this.currentStepValue++
      this._updateUI()
    }
  }

  previousStep() {
    const minStep = this._reportDataOnLeft ? 2 : 1
    if (this.currentStepValue > minStep) {
      this.currentStepValue--
      this._updateUI()
    }
  }

  goToStep(event) {
    const step = parseInt(event.currentTarget.dataset.step, 10)
    const minStep = this._reportDataOnLeft ? 2 : 1
    if (step >= minStep && step <= this.totalStepsValue) {
      this.currentStepValue = step
      this._updateUI()
    }
  }

  _onPreviewViewChanged(event) {
    this._reportDataOnLeft = event.detail.view === "report"
    if (this._reportDataOnLeft && this.currentStepValue === 1) {
      this.currentStepValue = 2
      this._updateUI()
    }
  }

  onRadioChange(event) {
    this._clearStepValidationErrors(event.target)
    this._calculateFailScore()
    this._highlightNoAnswers()
    this._toggleConditionalFields(event.target)
    this._updateStepState(event.target)
  }

  _updateUI() {
    this._showCurrentStep()
    this._updateStepTitle()
    this._updateStepCounter()
    this._updateNavigationButtons()
    this._updateStepperHighlight()
    this._scrollToTop()
  }

  _showCurrentStep() {
    this.stepTargets.forEach((stepEl) => {
      const stepNumber = parseInt(stepEl.dataset.qcFormStep, 10)
      stepEl.classList.toggle("hidden", stepNumber !== this.currentStepValue)
    })
  }

  _updateStepTitle() {
    if (this.hasStepTitleTarget) {
      this.stepTitleTarget.textContent = this.stepTitlesValue[this.currentStepValue] || ""
    }
  }

  _updateStepCounter() {
    if (this.hasStepCounterTarget) {
      this.stepCounterTarget.textContent = `Step ${this.currentStepValue} of ${this.totalStepsValue}`
    }
  }

  _updateNavigationButtons() {
    const isFirst = this.currentStepValue === 1
    const isLast = this.currentStepValue === this.totalStepsValue

    if (this.hasPrevButtonWrapperTarget) {
      this.prevButtonWrapperTarget.classList.toggle("hidden", isFirst)
    }
    if (this.hasNextButtonWrapperTarget) {
      this.nextButtonWrapperTarget.classList.toggle("hidden", isLast)
    }
    if (this.hasSaveButtonWrapperTarget) {
      this.saveButtonWrapperTarget.classList.toggle("hidden", !isLast)
    }
  }

  _updateStepperHighlight() {
    this._updateAllStepperSegments()
  }

  _calculateFailScore() {
    if (!this.hasFormTarget) return

    const allRadios = this.formTarget.querySelectorAll('input[type="radio"][value="false"]:checked')
    const count = allRadios.length

    if (this.hasFailScoreTarget) {
      this.failScoreTarget.textContent = `Fail Score: ${count}`
    }
  }

  _toggleConditionalFields(radio) {
    this.conditionalFieldTargets.forEach((field) => {
      if (field.dataset.parentField !== radio.name) return

      const shouldShow = radio.value === field.dataset.toggleValue

      if (shouldShow) {
        field.classList.remove("hidden")
      } else {
        field.classList.add("hidden")
        this._clearFieldInputs(field)
      }
    })
  }

  _clearFieldInputs(container) {
    container.querySelectorAll("input, textarea, select").forEach((input) => {
      if (input.type === "radio" || input.type === "checkbox") {
        input.checked = false
      } else {
        input.value = ""
      }
    })
    this._calculateFailScore()
  }

  _updateStepState(radio) {
    const stepEl = radio.closest('[data-qc-form-target="step"]')
    if (!stepEl) return

    const stepNumber = parseInt(stepEl.dataset.qcFormStep, 10)
    this._updateStepperSegmentColour(stepNumber)
  }

  _updateAllStepperSegments() {
    for (let i = 1; i <= this.totalStepsValue; i++) {
      this._updateStepperSegmentColour(i)
    }
  }

  _updateStepperSegmentColour(stepNumber) {
    const segment = this.stepperSegmentTargets[stepNumber - 1]
    if (!segment) return

    const circle = segment.querySelector("button")
    if (!circle) return

    const number = circle.querySelector("[data-stepper-number]")
    const check = circle.querySelector("[data-stepper-check]")
    const cross = circle.querySelector("[data-stepper-cross]")
    if (!number || !check || !cross) return

    circle.classList.remove(
      "bg-white", "bg-green-1000", "bg-red-1000",
      "bg-black-600", "bg-black-100", "hover:bg-black-200",
      "border-2", "border-brand-1000"
    )
    number.classList.remove("text-white", "text-black-600", "text-brand-1000")

    const isCurrent = stepNumber === this.currentStepValue
    const stepEl = this.stepTargets.find(
      (el) => parseInt(el.dataset.qcFormStep, 10) === stepNumber
    )
    if (!stepEl) return

    const hasValidationErrors = this._stepsWithErrors.has(stepNumber)
    const errorIconEl = this.stepperErrorTargets[stepNumber - 1]
    if (errorIconEl) {
      errorIconEl.classList.toggle("hidden", !hasValidationErrors)
    }

    if (hasValidationErrors && isCurrent) {
      circle.classList.add("bg-white", "border-2", "border-red-1000")
      number.classList.add("text-red-1000")
      number.classList.toggle("hidden", false)
      check.classList.toggle("hidden", true)
      cross.classList.toggle("hidden", true)
      return
    }

    if (hasValidationErrors && !isCurrent) {
      circle.classList.add("bg-red-1000")
      number.classList.add("text-white")
      number.classList.toggle("hidden", false)
      check.classList.toggle("hidden", true)
      cross.classList.toggle("hidden", true)
      return
    }

    const state = this._calculateStepState(stepEl)
    let showCheck = false
    let showCross = false

    if (state === "fail") {
      circle.classList.add("bg-red-1000")
      showCross = true
    } else if (isCurrent) {
      circle.classList.add("bg-white", "border-2", "border-brand-1000")
      number.classList.add("text-brand-1000")
    } else if (state === "pass") {
      circle.classList.add("bg-green-1000")
      showCheck = true
    } else if (state === "na") {
      circle.classList.add("bg-black-600")
      number.classList.add("text-white")
    } else {
      circle.classList.add("bg-black-100", "hover:bg-black-200")
      number.classList.add("text-black-600")
    }

    number.classList.toggle("hidden", showCheck || showCross)
    check.classList.toggle("hidden", !showCheck)
    cross.classList.toggle("hidden", !showCross)
  }

  _snapshotErrorSteps() {
    const steps = new Set()
    this.stepTargets.forEach((stepEl) => {
      const errorElements = stepEl.querySelectorAll('[data-has-error="true"]')
      const hasVisible = Array.from(errorElements).some((el) => {
        const conditionalParent = el.closest('[data-qc-form-target="conditionalField"]')
        return !conditionalParent || !conditionalParent.classList.contains("hidden")
      })
      if (hasVisible) steps.add(parseInt(stepEl.dataset.qcFormStep, 10))
    })
    return steps
  }

  _clearStepValidationErrors(input) {
    const stepEl = input.closest('[data-qc-form-target="step"]')
    if (!stepEl) return

    stepEl.querySelectorAll('[data-has-error="true"]').forEach((el) => {
      el.dataset.hasError = "false"
    })
    this._stepsWithErrors.delete(parseInt(stepEl.dataset.qcFormStep, 10))
  }

  _calculateStepState(stepEl) {
    const radioGroups = this._getRadioGroups(stepEl)
    const groupEntries = Object.entries(radioGroups)
    if (groupEntries.length === 0) return "empty"

    const visibleValues = []
    let hasUnanswered = false

    for (const [, radios] of groupEntries) {
      const isConditionallyHidden = radios.some((r) => {
        const conditionalParent = r.closest('[data-qc-form-target="conditionalField"]')
        return conditionalParent && conditionalParent.classList.contains("hidden")
      })
      if (isConditionallyHidden) continue

      const checkedRadio = radios.find((r) => r.checked)
      if (!checkedRadio) {
        hasUnanswered = true
        continue
      }

      visibleValues.push(checkedRadio.value)
    }

    if (visibleValues.includes("false")) return "fail"
    if (hasUnanswered || visibleValues.length === 0) return "empty"
    if (visibleValues.includes("true")) return "pass"
    if (visibleValues.every((v) => v === "N/A")) return "na"

    return "empty"
  }

  _highlightNoAnswers() {
    if (!this.hasFormTarget) return

    const radioGroups = this.formTarget.querySelectorAll('[data-controller="radio-group"]')
    const highlightClasses = ["bg-red-50", "rounded-12", "p-16"]

    radioGroups.forEach((group) => {
      const hasFalseChecked = group.querySelector('input[type="radio"][value="false"]:checked')

      if (hasFalseChecked) {
        group.classList.add(...highlightClasses)
      } else {
        group.classList.remove(...highlightClasses)
      }
    })
  }

  _navigateToFirstErrorStep() {
    if (!this.hasErrorSummaryTarget) return

    for (let i = 2; i <= this.totalStepsValue; i++) {
      const stepEl = this.stepTargets.find(
        (el) => parseInt(el.dataset.qcFormStep, 10) === i
      )
      if (!stepEl) continue

      const errorElements = stepEl.querySelectorAll('[data-has-error="true"]')
      const hasVisibleError = Array.from(errorElements).some((el) => {
        const conditionalParent = el.closest('[data-qc-form-target="conditionalField"]')
        return !conditionalParent || !conditionalParent.classList.contains("hidden")
      })

      if (hasVisibleError) {
        this.currentStepValue = i
        return
      }
    }
  }

  _getRadioGroups(container) {
    const groups = {}
    container.querySelectorAll('input[type="radio"]').forEach((radio) => {
      if (!groups[radio.name]) groups[radio.name] = []
      groups[radio.name].push(radio)
    })
    return groups
  }

  _scrollToTop() {
    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.scrollTop = 0
    }
  }

  _onKeydown(event) {
    if (!event.ctrlKey && !event.metaKey) return

    const active = document.activeElement
    const tag = active?.tagName?.toLowerCase()
    if (tag === "textarea" || tag === "select" || (tag === "input" && !["radio", "checkbox"].includes(active?.type))) return

    if (event.key === "ArrowRight") {
      event.preventDefault()
      this.nextStep()
    } else if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.previousStep()
    }
  }
}
